/**
 * "Take me to where this candidate's next action is done."
 *
 * The Overdue tab names what is blocking someone; every actionable chip there is a link to the
 * place that unblocks it (slaChipTarget picks the destination, useFocusCard does the landing).
 * This is the middle piece: it moves the tab, the scheduling ROUND, and the focus together.
 *
 * The round matters as much as the tab. Switching to 📅 Scheduling alone lands on whichever round
 * was last open, which is a different candidate's list — the link would look broken for the exact
 * reason it was added.
 *
 * ── A focus must not outlive the trip it was made for ────────────────────────────────────────
 *
 * The stored target REMEMBERS WHERE IT WAS FOR, and `focus` is null anywhere else. That is
 * derived rather than cleared by each navigation handler, because the tab lives in the URL
 * (`useBoardTab`): browser Back, Forward and a pasted link all change it without going through
 * any handler here, while TAActionBoardView stays mounted. The destination tab then re-mounts
 * with the old value still set and useFocusCard expands, scrolls to and re-rings a candidate the
 * recruiter stopped looking at several clicks ago.
 *
 * Deriving it means one rule covers every way the board can move, including the ones that do not
 * call this hook at all. `goToTab` / `goToRound` still drop the stored value — belt and braces,
 * and they are the honest name for "a navigation that names no candidate".
 *
 * Its own file because TAActionBoardView sits on the repo's 500-line ceiling.
 */
import { useCallback, useMemo, useState } from 'react';
import type { BoardDestination } from './slaChipTarget';
import type { FocusTarget } from './useFocusCard';
import type { TabId } from './BoardChrome';
import type { SchedulingSubTab } from './SchedulingGroup';

/** A focus target plus the place it belongs to. `round` is null for destinations outside 📅. */
interface StoredFocus extends FocusTarget {
  tab: TabId;
  round: SchedulingSubTab | null;
}

export function useBoardNavigation({
  tab,
  round,
  setTab,
  setRound,
}: {
  /** Where the board IS — from the URL, so it changes without anything here being called. */
  tab: TabId;
  round: SchedulingSubTab;
  setTab: (tab: TabId) => void;
  setRound: (round: SchedulingSubTab) => void;
}) {
  const [stored, setStored] = useState<StoredFocus | null>(null);

  /**
   * Memoised on the fields, not the object: `useFocusCard`'s effect depends on this identity, and
   * a fresh object every render would re-scroll the page on every keystroke elsewhere in the view.
   */
  const focus = useMemo<FocusTarget | null>(() => {
    if (!stored) return null;
    if (stored.tab !== tab) return null;
    if (stored.round !== null && stored.round !== round) return null;
    return { postingId: stored.postingId, opportunityId: stored.opportunityId };
  }, [stored, tab, round]);

  const goToCandidate = useCallback(
    (dest: BoardDestination, postingId: string, opportunityId: string) => {
      setTab(dest.tab);
      if (dest.tab === 'scheduling') setRound(dest.round);
      setStored({
        postingId,
        opportunityId,
        tab: dest.tab,
        round: dest.tab === 'scheduling' ? dest.round : null,
      });
    },
    [setTab, setRound]
  );

  const goToTab = useCallback(
    (next: TabId) => {
      setStored(null);
      setTab(next);
    },
    [setTab]
  );

  const goToRound = useCallback(
    (next: SchedulingSubTab, nextTab?: TabId) => {
      setStored(null);
      if (nextTab) setTab(nextTab);
      setRound(next);
    },
    [setTab, setRound]
  );

  /** Drop the target without navigating — the Feedback tab's "clear focus". */
  const clearFocus = useCallback(() => setStored(null), []);

  return { focus, clearFocus, goToCandidate, goToTab, goToRound };
}
